import { useState } from 'react';
import classNames from 'classnames';
import { useField } from 'formik';
import IconSVG from '../../styles/svg-icons';
import { FormError, MultiSelect, MultiSelectDataItem, MultiSelectProps } from '../controls';

interface MultiSelectFieldWithAddNewProps extends Omit<MultiSelectProps,
    'sourceItems' | 'showAddButton' | 'className' | 'onItemSelect' | 'onResetAll'> {
    name: string;
    label: string;
    /** Selectable items — the selection state comes from the Formik field value */
    dataItems: MultiSelectDataItem[];
    wrapperClassName?: string;
    maxLength?: number;
    onAddNew?: (value: string) => void;
}

export function MultiSelectFieldWithAddNew({
    name,
    label,
    dataItems,
    wrapperClassName,
    maxLength = 64,
    onAddNew,
    placeholder = 'Select',
    /** Marks the label with a red asterisk and requires a selection on submit */
    required = false,
    showResetButton = false,
    ...multiSelectProps
}: MultiSelectFieldWithAddNewProps) {
    const [field, meta, helpers] = useField<string[] | undefined>(name);
    const [addMode, setAddMode] = useState(false);
    const [draft, setDraft] = useState('');

    const selectedValues = field.value || [];

    const optionValues = new Set(dataItems.map(o => String(o.value)));
    const [addedValues, setAddedValues] = useState<string[]>(
        () => selectedValues.filter(v => !optionValues.has(v)),
    );

    const allOptions: MultiSelectDataItem[] = [
        ...dataItems,
        ...addedValues
            .filter(v => !optionValues.has(v))
            .map(v => ({ value: v, text: v })),
    ].sort((a, b) => a.text.localeCompare(b.text));

    const handleSelectionChange = (item: MultiSelectDataItem, selected: boolean) => {
        const value = String(item.value);
        const next = selected
            ? Array.from(new Set([...selectedValues, value]))
            : selectedValues.filter(v => v !== value);
        helpers.setValue(next);
    };

    const handleNewItemClick = () => {
        setAddMode(true);
        setDraft('');
    };

    const handleNewItemCancel = () => {
        setAddMode(false);
        setDraft('');
    };

    const handleNewItemSave = () => {
        const trimmed = draft.trim();

        if (!trimmed) return;
        if (!selectedValues.includes(trimmed)) {
            helpers.setValue([...selectedValues, trimmed]);
        }
        if (!optionValues.has(trimmed) && !addedValues.includes(trimmed)) {
            setAddedValues([...addedValues, trimmed]);
        }
        if (onAddNew && !optionValues.has(trimmed)) {
            onAddNew(trimmed);
        }
        setAddMode(false);
        setDraft('');
    };

    const withError = meta.touched && meta.error;

    return (
        <div className={classNames('data-item-row', wrapperClassName)} onClick={e => e.stopPropagation()}>
            {label && (
                <label className="data-item-col data-item-col-title data-item-capitalize">
                    {label}
                    {required && <span className="text-red"> *</span>}
                </label>
            )}
            <div className="data-item-col">
                {addMode ? (
                    <div className="loan-add-new-row">
                        <input
                            className="form-control"
                            value={draft}
                            maxLength={maxLength}
                            onChange={e => setDraft(e.target.value)}
                            placeholder="Add New"
                            autoFocus
                        />
                        <button
                            type="button"
                            className="btn-link loan-add-new-btn loan-add-new-btn-save"
                            onClick={handleNewItemSave}
                            disabled={!draft.trim()}
                            aria-label="Save"
                        >
                            <IconSVG name="saved" width={16} height={16} />
                        </button>
                        <button
                            type="button"
                            className="btn-link loan-add-new-btn loan-add-new-btn-close"
                            onClick={handleNewItemCancel}
                            aria-label="Cancel"
                        >
                            <IconSVG name="close" width={16} height={16} />
                        </button>
                    </div>
                ) : (
                    <div className="loan-multiselect-with-add">
                        <MultiSelect
                            {...multiSelectProps}
                            className={classNames({ 'is-invalid': withError })}
                            sourceItems={allOptions.map(o => ({
                                ...o,
                                selected: selectedValues.includes(String(o.value)),
                            }))}
                            showAddButton={false}
                            placeholder={placeholder}
                            required={required}
                            showResetButton={showResetButton}
                            onItemSelect={handleSelectionChange}
                            onResetAll={() => helpers.setValue([])}
                        />
                        <button
                            type="button"
                            className="btn-link loan-add-new-btn loan-add-new-btn-plus"
                            onClick={handleNewItemClick}
                            aria-label="Add new"
                        >
                            <IconSVG name="plus" width={16} height={16} />
                        </button>
                    </div>
                )}
                {withError && <FormError message={meta.error} />}
            </div>
        </div>
    );
}
